import { useEffect, useLayoutEffect, useRef, useState, useId } from 'preact/hooks';
import { describedBy, FieldHeader } from './FieldHeader';
import { Tooltip } from './Tooltip';
import type { EventDateRange } from '../../shared/types';
import { isValidDate } from '../../shared/validate';
import { monthDays, parseDay, parseTypedDate, parseTypedRange, selectedRange, shiftDay, shiftMonth, type TwoDigitYears } from '../lib/dateRange';
import { formatUsDate } from '../../shared/builtin';
import { AppIcon } from './AppIcon';

/** The height of the bar Safari shows over an iPhone's keyboard, with its arrows and check mark. */
const KEYBOARD_BAR = 56;
/** How much of an iPhone's screen its keyboard leaves showing, measured on one: 431 of 667 points. */
const KEYBOARD_LEAVES = 0.646;
/** The calendar's height in the page, for planning before it shows: five weeks, as most months take, until measured. */
let calendarHeight = 320;
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = Array.from({ length: 12 }, (_, i) => new Date(2026, i, 1).toLocaleDateString(undefined, { month: 'long' }));

/**
 * A box to type a day or range of days into, which shows a calendar to choose them in while it has focus. What can't be
 * read as dates is passed on as typed, as the start, to be reported as invalid.
 */
export function DateRangePicker({ value, onChange, label, error, onTouched, mode = 'range', maxDate, help, question }: {
  value: EventDateRange; onChange: (value: EventDateRange) => void; label: string; error?: string; onTouched: () => void; mode?: 'range' | 'single'; maxDate?: string; help?: string;
  /** For a question, which shows its own label, help and error: the box's id for the label, and what describes it. */
  question?: { id: string; describedBy?: string; required?: boolean; years: TwoDigitYears };
}) {
  const id = useId();
  // Help under the dates; an empty string shows none.
  const helpText = help ?? 'Type or choose one day, or a start and end date for a longer activity, like 6/12/2026 – 6/13/2026.';
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const initial = isValidDate(value.start) ? value.start : today;
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(initial.slice(0, 7));
  const [focused, setFocused] = useState(initial);
  const [first, setFirst] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  // A range can also be dragged: press on one day, release on another.
  const [dragFrom, setDragFrom] = useState<string | null>(null);
  const suppressClick = useRef(false);
  const [position, setPosition] = useState<{ left: number; top: number; maxHeight?: number }>({ left: 16, top: 16 });
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLInputElement>(null);
  const calendar = useRef<HTMLDivElement>(null);
  // Moving through the days with the arrow keys moves focus along.
  const focusDay = useRef(false);
  // The calendar's controls are Tab stops only once focus has gone into it, so Tab from the box goes on to the next
  // field, closing the calendar opened by a click or by typing.
  const [inside, setInside] = useState(false);
  const stop = inside ? undefined : -1;
  // Focus put back in the box on closing doesn't open the calendar again.
  const refocusing = useRef(false);
  const close = (restore = false) => {
    setOpen(false); onTouched();
    if (restore) { refocusing.current = true; trigger.current?.focus({ preventScroll: true }); refocusing.current = false; }
  };
  const read = (typing: string): EventDateRange | null => {
    if (mode === 'range') return parseTypedRange(typing, today);
    const day = parseTypedDate(typing, today, question?.years);
    return day ? { start: day } : null;
  };
  const shown = (range: EventDateRange) => !isValidDate(range.start) ? range.start
    : range.end && range.end !== range.start ? `${formatUsDate(range.start)} – ${formatUsDate(range.end)}` : formatUsDate(range.start);
  const keyOf = (range: EventDateRange) => `${range.start}|${range.end ?? ''}`;
  const [text, setText] = useState(() => shown(value));
  // Dates chosen in the calendar or cleared show in the box; ones being typed stay as typed.
  useEffect(() => { if (keyOf(read(text) ?? { start: text.trim() }) !== keyOf(value)) setText(shown(value)); }, [value.start, value.end]);
  /** What was typed, shown the usual way when it can be read. */
  const tidy = () => { const range = read(text); if (range) setText(shown(range)); };

  const place = () => {
    // A scroll can arrive after the calendar has closed, before its listeners are gone.
    if (!calendar.current || !trigger.current) return;
    // On a phone or touch screen the calendar opens in the page, under the box (see the stylesheet): one floating over
    // the page can't be kept in step with Safari's keyboard, which moves the visible part of the page as it opens.
    if (getComputedStyle(calendar.current).position !== 'fixed') return;
    const rect = trigger.current.getBoundingClientRect();
    const width = Math.min(340, innerWidth - 32);
    // The calendar goes under the box being typed into. Without room for all of it, the page scrolls the field to the
    // top, and the calendar scrolls within the rest.
    const field = root.current!.getBoundingClientRect().top;
    let below = rect.bottom + 8;
    if (innerHeight - below - 8 < calendar.current.scrollHeight && field > 16) {
      window.scrollBy(0, field - 16);
      below = trigger.current.getBoundingClientRect().bottom + 8;
    }
    setPosition({ left: Math.max(16, Math.min(rect.left, innerWidth - width - 16)), top: below, maxHeight: Math.max(160, innerHeight - below - 8) });
  };
  // In the page, the calendar is brought into view as far as it goes while its box stays in sight, when it opens and
  // again once a phone's keyboard has come up and covered part of the page.
  const reveal = () => {
    if (!calendar.current || !root.current || getComputedStyle(calendar.current).position === 'fixed') return;
    // While a phone's keyboard is up, the iPhone's bar over it (arrows and a check mark) covers the bottom of what it
    // reports as visible, so that is left out too.
    const viewport = window.visualViewport, keyboard = !!viewport && viewport.height < innerHeight - 100;
    const visible = (viewport?.height ?? innerHeight) - (keyboard ? KEYBOARD_BAR : 0), hidden = calendar.current.getBoundingClientRect().bottom + 8 - visible;
    // The box itself stays in sight; its label may go off the top to show more of the calendar.
    calendarHeight = calendar.current.offsetHeight;
    if (hidden > 0) window.scrollBy({ top: Math.min(hidden, trigger.current!.getBoundingClientRect().top - 8), behavior: 'smooth' });
  };
  // A tap that gives the box focus scrolls the page at once to where the box and the calendar will fit above the
  // keyboard, then focuses the box itself without the browser's own scroll: Safari works that out as the box gets focus
  // and carries it out after any other, so the page would move twice. The page moves once, as the finger lifts. A touch
  // that moves is a scroll, left to the browser, as is a tap on a box that already has focus, placing the caret. The
  // keyboard's height is known only once it's up, so until then it's taken as an iPhone's.
  const touch = useRef<{ x: number; y: number } | null>(null);
  // When a finger or mouse last pressed inside the field or its calendar.
  const pressed = useRef(0);
  const makeRoom = () => {
    if (!trigger.current || !matchMedia('(max-width: 640px), (pointer: coarse)').matches) return;
    const viewport = window.visualViewport, keyboard = !!viewport && viewport.height < innerHeight - 100;
    const visible = (keyboard ? viewport!.height : innerHeight * KEYBOARD_LEAVES) - KEYBOARD_BAR;
    const box = trigger.current.getBoundingClientRect(), hidden = box.bottom + 4 + calendarHeight + 8 - visible;
    if (hidden > 0) window.scrollBy(0, Math.min(hidden, box.top - 8));
  };
  // Safari scrolls the page itself to bring a box being typed into in sight, and again while its keyboard comes up. The
  // calendar waits until that has stopped for a moment, then scrolls once, smoothly, rather than taking turns with it.
  // Only until the participant touches the page or turns a scroll wheel, though: from then on any movement is theirs,
  // including the visible part changing size as Safari's toolbar comes and goes, and the page stays where they put it.
  useEffect(() => {
    if (!open) return;
    const viewport = window.visualViewport, opening = Date.now() + 1500;
    let timer: ReturnType<typeof setTimeout> | undefined, waiting = true, theirs = false;
    const later = () => { if (theirs) return; clearTimeout(timer); timer = setTimeout(() => { waiting = false; reveal(); }, 250); };
    const resized = () => { if (Date.now() < opening) { waiting = true; later(); } };
    const scrolled = () => { if (waiting) later(); };
    const handedOver = (e: Event) => { if (e.target !== trigger.current) { theirs = true; clearTimeout(timer); } };
    later();
    viewport?.addEventListener('resize', resized);
    viewport?.addEventListener('scroll', scrolled);
    window.addEventListener('scroll', scrolled);
    for (const type of ['touchstart', 'wheel']) document.addEventListener(type, handedOver, { capture: true, passive: true });
    return () => {
      clearTimeout(timer); viewport?.removeEventListener('resize', resized); viewport?.removeEventListener('scroll', scrolled); window.removeEventListener('scroll', scrolled);
      for (const type of ['touchstart', 'wheel']) document.removeEventListener(type, handedOver, { capture: true });
    };
  }, [open]);
  useLayoutEffect(() => { if (open) place(); }, [open, month, value.start, label]);
  const openCalendar = (typing = text) => {
    const start = read(typing)?.start ?? '';
    const day = isValidDate(start) && (!maxDate || start <= maxDate) ? start : maxDate ?? today;
    setFocused(day); setMonth(day.slice(0, 7)); setFirst(null); setHovered(null); setInside(false); setOpen(true);
  };
  useEffect(() => {
    if (open && focusDay.current) {
      calendar.current?.querySelector<HTMLButtonElement>(`[data-day="${focused}"]`)?.focus({ preventScroll: true });
      focusDay.current = false;
    }
  }, [open, month, focused]);
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => { if (!e.composedPath().includes(root.current!)) close(); };
    // A mouse press outside closes it at once; a finger only on a tap, since touching the page also starts a scroll.
    const press = (e: PointerEvent) => { if (e.pointerType === 'mouse') outside(e); };
    const tap = (e: MouseEvent) => { if ((e as PointerEvent).pointerType !== 'mouse') outside(e); };
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); } };
    // The floating calendar follows the box as the page scrolls or the window resizes.
    const resize = () => place();
    document.addEventListener('pointerdown', press);
    document.addEventListener('click', tap);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', resize);
    return () => {
      document.removeEventListener('pointerdown', press);
      document.removeEventListener('click', tap);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', resize);
    };
  }, [open]);

  useEffect(() => {
    if (!dragFrom) return;
    const dayAt = (e: PointerEvent) => {
      const day = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest<HTMLElement>('.date-calendar [data-day]')?.dataset.day;
      return day && (!maxDate || day <= maxDate) ? day : undefined;
    };
    const move = (e: PointerEvent) => { const day = dayAt(e); if (day) setHovered(day); };
    const up = (e: PointerEvent) => {
      const day = dayAt(e); setDragFrom(null);
      if (!day || day === dragFrom) return;
      // Releasing on another day finishes the range; the click that may follow on the pressed day must not restart it.
      suppressClick.current = true; setTimeout(() => { suppressClick.current = false; });
      onChange(selectedRange(dragFrom, day)); setFocused(day); setFirst(null); setHovered(null); close(true);
    };
    const cancel = () => { setDragFrom(null); setHovered(null); };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
    };
  }, [dragFrom]);

  const select = (day: string) => {
    if (maxDate && day > maxDate) return;
    setFocused(day);
    if (mode === 'single') { onChange({ start: day }); close(true); return; }
    if (first) { onChange(selectedRange(first, day)); setFirst(null); setHovered(null); close(true); }
    else { setFirst(day); onChange({ start: day }); }
  };
  const moveFocus = (day: string) => {
    const next = maxDate && day > maxDate ? maxDate : day;
    focusDay.current = true; setFocused(next); setMonth(next.slice(0, 7));
  };
  const from = dragFrom ?? first;
  const range = from && hovered ? selectedRange(from, hovered) : value;
  const year = Number(month.slice(0, 4));
  const currentYear = new Date().getFullYear();
  const firstYear = Math.min(currentYear - (mode === 'single' ? 120 : 5), year);
  const lastYear = maxDate ? Number(maxDate.slice(0, 4)) : Math.max(currentYear + 10, year);
  const years = Array.from({ length: lastYear - firstYear + 1 }, (_, i) => firstYear + i);
  return (
    <div class="date-range" ref={root} data-error={!!error} onPointerDown={() => { pressed.current = Date.now(); }}>
      {!question && <FieldHeader labelId={`${id}-label`} labelClass="label" label={<>{label}<span class="required-label">Required</span></>} error={error} errorId={`${id}-error`} missing={!value.start.trim()} />}
      <div class="date-input"><AppIcon name="calendar" />
        <input type="text" ref={trigger} id={question?.id} aria-labelledby={question ? undefined : `${id}-label`} placeholder="MM/DD/YYYY" autoComplete={mode === 'single' && !question ? 'bday' : 'off'} value={text}
          aria-haspopup="dialog" aria-expanded={open} aria-controls={`${id}-calendar`} aria-invalid={!!error} aria-required={question?.required}
          aria-describedby={question ? question.describedBy : describedBy(error, `${id}-error`, helpText && `${id}-help`)}
          onTouchStart={(e) => { const point = e.touches[0]; touch.current = document.activeElement !== e.currentTarget && e.touches.length === 1 ? { x: point.clientX, y: point.clientY } : null; }}
          onTouchEnd={(e) => {
            const start = touch.current, point = e.changedTouches[0]; touch.current = null;
            if (!start || Math.hypot(point.clientX - start.x, point.clientY - start.y) > 10) return;
            e.preventDefault();
            const box = e.currentTarget;
            makeRoom(); box.focus({ preventScroll: true }); box.setSelectionRange(box.value.length, box.value.length);
            if (!open) openCalendar();
          }}
          onClick={() => { if (!open) openCalendar(); }}
          // On a touch screen, focus arriving another way than a tap, as by the arrows over an iPhone's keyboard, opens
          // the calendar too: the arrows can't step into it, so it doesn't hold them up as it would Tab on a computer.
          onFocus={() => { if (!open && !refocusing.current && Date.now() - pressed.current > 500 && matchMedia('(pointer: coarse)').matches) openCalendar(); }}
          onBlur={(e) => {
            tidy();
            // Focus leaving for nowhere, as when a phone's keyboard is put away, closes the calendar too. A press in it
            // takes focus from the box as well, on an iPhone, whose buttons don't take it, so that one leaves it open.
            const into = e.relatedTarget instanceof Node && root.current?.contains(e.relatedTarget);
            if (open && !into && Date.now() - pressed.current > 500) close();
          }}
          onInput={(e) => {
            const next = e.currentTarget.value, range = read(next);
            setText(next); onChange(range ?? { start: next.trim() });
            // The calendar follows along to dates that can be read, and a range half chosen in it starts over.
            if (!open) openCalendar(next);
            else if (range) { const day = maxDate && range.start > maxDate ? maxDate : range.start; setFocused(day); setMonth(day.slice(0, 7)); setFirst(null); setHovered(null); }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); tidy(); if (open) close(); }
            // The Down arrow goes into the calendar, opening it first if need be.
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (open) calendar.current?.querySelector<HTMLButtonElement>(`[data-day="${focused}"]`)?.focus({ preventScroll: true });
              else { openCalendar(); focusDay.current = true; }
            }
          }} /></div>
      {open && (
        <div class="date-calendar" id={`${id}-calendar`} ref={calendar} role="dialog" aria-label={`${label} calendar`} onFocusIn={() => setInside(true)}
          style={{ left: `${position.left}px`, top: `${position.top}px`, maxHeight: position.maxHeight ? `${position.maxHeight}px` : undefined }}>
          <div class="calendar-heading">
            <Tooltip text="Previous month"><button type="button" class="icon calendar-nav" tabIndex={stop} aria-label="Previous month" onClick={() => { const m = shiftMonth(month, -1); setMonth(m); setFocused(`${m}-01`); }}>‹</button></Tooltip>
            <div class="calendar-month-selects">
              <select tabIndex={stop} aria-label="Month" value={Number(month.slice(5)) - 1} onChange={(e) => { const requested = `${year}-${String(Number(e.currentTarget.value) + 1).padStart(2, '0')}`; const m = maxDate && requested > maxDate.slice(0, 7) ? maxDate.slice(0, 7) : requested; setMonth(m); setFocused(`${m}-01`); }}>{MONTHS.map((name, i) => <option value={i}>{name}</option>)}</select>
              <select tabIndex={stop} aria-label="Year" value={year} onChange={(e) => { const requested = `${e.currentTarget.value}-${month.slice(5)}`; const m = maxDate && requested > maxDate.slice(0, 7) ? maxDate.slice(0, 7) : requested; setMonth(m); setFocused(`${m}-01`); }}>{years.map((y) => <option value={y}>{y}</option>)}</select>
            </div>
            <Tooltip text="Next month"><button type="button" class="icon calendar-nav" tabIndex={stop} disabled={!!maxDate && month >= maxDate.slice(0, 7)} aria-label="Next month" onClick={() => { const m = shiftMonth(month, 1); setMonth(m); setFocused(`${m}-01`); }}>›</button></Tooltip>
          </div>
          <div class={`calendar-grid${mode === 'range' ? ' range' : ''}`} role="grid" aria-label={parseDay(`${month}-01`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })}>
            <div role="row" class="calendar-week">{WEEKDAYS.map((d) => <span role="columnheader">{d}</span>)}</div>
            {monthDays(month).filter(week => week.some(day => day.startsWith(month))).map((week) => <div role="row" class="calendar-week">{week.map((day) => {
              const selected = day >= range.start && day <= (range.end || range.start);
              const endpoint = day === range.start || day === range.end;
              return <button type="button" role="gridcell" data-day={day} disabled={!!maxDate && day > maxDate} tabIndex={inside && day === focused ? 0 : -1}
                aria-selected={selected} aria-current={day === today ? 'date' : undefined}
                aria-label={parseDay(day).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                class={`calendar-day${day.slice(0, 7) !== month ? ' other-month' : ''}${selected ? ' selected' : ''}${endpoint ? ' endpoint' : ''}`}
                onPointerDown={(e) => { if (mode === 'range' && e.isPrimary && e.button === 0 && !(maxDate && day > maxDate)) { setDragFrom(day); setHovered(day); } }}
                onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } select(day); }} onMouseEnter={() => { if (mode === 'range') setHovered(day); }} onFocus={() => setFocused(day)}
                onKeyDown={(e) => {
                  const steps: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
                  if (e.key in steps) { e.preventDefault(); moveFocus(shiftDay(day, steps[e.key])); }
                  if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); moveFocus(shiftDay(day, (e.key === 'Home' ? 0 : 6) - parseDay(day).getUTCDay())); }
                  if (e.key === 'PageUp' || e.key === 'PageDown') { e.preventDefault(); moveFocus(`${shiftMonth(month, e.key === 'PageUp' ? -1 : 1)}-01`); }
                }}>{parseDay(day).getUTCDate()}</button>;
            })}</div>)}
          </div>
          <p class="calendar-instruction sr-only" role="status">{mode === 'single' ? 'Type the date, or choose the month and year, then select the day.' : first ? 'One day selected. Choose another date for a range, or press Done.' : 'Type the dates, or choose a day. Select a second date, or drag from one date to another, to make a range.'}</p>
          <div class="row between calendar-footer">
            <button type="button" class="link" tabIndex={stop} onClick={() => { onChange({ start: '' }); close(true); }}>Clear date</button>
            <button type="button" tabIndex={stop} onClick={() => close(true)}>Done</button>
          </div>
        </div>
      )}
      {helpText && !question && <span class="field-help" id={`${id}-help`}>{helpText}</span>}
    </div>
  );
}

/** A single date, typed or chosen in the same calendar as event dates. */
export function DatePicker({ value, onChange, label, error, maxDate, help }: {
  value: string; onChange: (value: string) => void; label: string; error?: string; maxDate?: string; help?: string;
}) {
  // Its errors come from the registration step's checks, shown once the field is left, as every field's are.
  return <DateRangePicker mode="single" value={{ start: value }} onChange={(date) => onChange(date.start)}
    label={label} error={error} maxDate={maxDate} help={help ?? ''} onTouched={() => {}} />;
}
